import React, { PropsWithChildren } from 'react';
import styled from 'styled-components';
import { Badge, BadgeType } from '../../badge';
import { breakpoint, theme } from '../../../theme';
import { ConditionalWrapper } from '../../../utils';
import { SvgComponent } from '../../../assets';

export type ActionCardProps = {
  badgeLabel?: string;
  badgeType?: BadgeType;
  imageUrl: string;
  imageHref?: string;
  imageOnClick?: () => void;
  icon?: SvgComponent;
  iconOnClick?: () => void;
};

const StyledActionCard = styled.article`
  border: 1px solid ${theme.colors.neutrals.solidGrays[6]};
  border-radius: ${theme.borderRadius.default};
  display: flex;
  flex-direction: column;

  ${breakpoint('sm')`
    flex-direction: row;
  `}
`;

const BackgroundImageContainer = styled.figure<{ imageUrl: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: pointer;

  background-image: ${({ imageUrl }) => `url('${imageUrl}')`};
  background-position: center;
  background-size: contain;
  background-repeat: no-repeat;

  height: 224px;
  width: 100%;
  margin: 0;
  overflow: hidden;

  ${breakpoint('sm')`
    height: 100%;
    min-height: 134px;
    width: 235px;
  `}
`;

const StyledBadge = styled(Badge)`
  left: 0;
  position: absolute;
  top: 12px;
`;

const ContentWrapper = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-flow: column wrap;
  justify-content: space-between;
  width: 100%;
  padding: ${theme.spacings[3].rem};

  ${breakpoint('sm')`
    padding: ${theme.spacings[4].rem} ${theme.spacings[5].rem};
  `};
`;

export const ActionCard: React.FC<PropsWithChildren<ActionCardProps>> = ({
  badgeLabel,
  badgeType,
  imageUrl,
  imageHref,
  imageOnClick,
  icon,
  iconOnClick,
  children,
}) => (
  <StyledActionCard>
    <ConditionalWrapper
      condition={!imageOnClick}
      wrapper={(children: JSX.Element) => <a href={imageHref}>{children}</a>}
      falseWrapper={(children: JSX.Element) => <div>{children}</div>}
    >
      <BackgroundImageContainer imageUrl={imageUrl} onClick={imageOnClick}>
        {badgeLabel && (
          <StyledBadge
            label={badgeLabel}
            type={badgeType}
            icon={icon}
            iconOnClick={iconOnClick}
          />
        )}
      </BackgroundImageContainer>
    </ConditionalWrapper>
    <ContentWrapper>{children}</ContentWrapper>
  </StyledActionCard>
);
